import { matchRoutes } from 'react-router-config';
import { listActions } from '@plone/volto/actions/actions/actions';
import { getBreadcrumbs } from '@plone/volto/actions/breadcrumbs/breadcrumbs';
import { Contents } from '@plone/volto/components/manage/Contents';
import { getBaseUrl, stripSubpathPrefix } from '@plone/volto/helpers/Url/Url';

/**
 * Whether the router is about to show the contents view.
 * @function isContentsView
 * @param {Array} routes Route config of the app
 * @param {string} pathname Pathname of the new location
 * @returns {boolean} Whether the route renders the contents view
 */
export const isContentsView = (routes, pathname) =>
  matchRoutes(routes, stripSubpathPrefix(pathname)).some(
    ({ route }) => route.component === Contents,
  );

/**
 * Whether the router is about to show the contents view without knowing its
 * asyncConnect. The view is loaded lazily, and loadable hands its statics,
 * among them the asyncConnect that loads the actions of the folder, to the
 * route only once the view has been rendered.
 * @function isUnknownContentsView
 * @param {Array} routes Route config of the app
 * @param {string} pathname Pathname of the new location
 * @returns {boolean} Whether the actions have to be loaded for the view
 */
export const isUnknownContentsView = (routes, pathname) =>
  !Contents.reduxAsyncConnect && isContentsView(routes, pathname);

/**
 * asyncConnect item of the App that loads the actions of the folder before
 * the router shows the contents view for the first time. Otherwise the view
 * checks the actions of the previous page and shows "Nicht autorisiert" to
 * members who may upload into the folder (#22811). It also loads the
 * breadcrumbs of the folder on every way into the view: the view requests no
 * content, which would bring them along, so it showed the breadcrumbs of the
 * previous page (#22811). On the server the content request of the App
 * brings both along.
 */
export const contentsActions = {
  key: 'contentsActions',
  promise: ({ location, routes, store: { dispatch } }) => {
    if (!__CLIENT__ || !isContentsView(routes, location.pathname)) {
      return null;
    }
    const path = getBaseUrl(location.pathname);
    // settled, not all: a failed request must not let the router show the
    // view before the actions are there
    return Promise.allSettled([
      dispatch(getBreadcrumbs(path)),
      ...(isUnknownContentsView(routes, location.pathname)
        ? [dispatch(listActions(path))]
        : []),
    ]);
  },
};
